import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, AuthContext } from './AuthContext';
import LoginPage from './pages/LoginPage';
import SuperAdminSetupPage from './pages/SuperAdminSetupPage';
import UserActivationPage from './pages/UserActivationPage';
import UserManagementPage from './pages/UserManagementPage';
import UnmatchedEmailsPage from './pages/UnmatchedEmailsPage';
import CompanySelectionPage from './pages/CompanySelectionPage';
import DashboardPage from './pages/DashboardPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import FlowDesignerPage from './pages/FlowDesignerPage';
import ConverterDetailPage from './pages/ConverterDetailPage';
import ReviewFilePage from './pages/ReviewFilePage';
import ProjectExecutionResults from './pages/ProjectExecutionResults';
import ProjectExecutionSteps from './pages/ProjectExecutionSteps';
import AdminEscalationsPage from './pages/AdminEscalationsPage';
import NavBar from './components/NavBar';

// Client-side components (moved to PWA in /client)

// Admin client management
import ClientUsersPage from './pages/ClientUsersPage';

const PrivateRoute = ({ children }) => {
  const { user, loading } = React.useContext(AuthContext);
  if (loading) return null;
  return user ? children : <Navigate to="/login" />;
};



const AppRoutes = () => {
  const location = useLocation();
  const hideNav = ['/login', '/setup-super-admin', '/activate'].includes(location.pathname);
  
  return (
    <>
      {!hideNav && <NavBar />}
      <Routes>
        {/* Authentication Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/setup-super-admin" element={<SuperAdminSetupPage />} />
        <Route path="/activate" element={<UserActivationPage />} />
        
        {/* User Management Routes */}
        <Route path="/users" element={
          <PrivateRoute>
            <UserManagementPage />
          </PrivateRoute>
        } />
        <Route path="/unmatched-emails" element={
          <PrivateRoute>
            <UnmatchedEmailsPage />
          </PrivateRoute>
        } />
        <Route path="/escalations" element={
          <PrivateRoute>
            <AdminEscalationsPage />
          </PrivateRoute>
        } />
        
        {/* Client Routes - moved to PWA in /client */}
        
        {/* Admin Company Management Routes */}
        <Route path="/company/:companyId/client-users" element={
          <PrivateRoute>
            <ClientUsersPage />
          </PrivateRoute>
        } />
        <Route path="/company/:companyId/project/:projectId/execution/:executionId/results" element={
          <PrivateRoute>
            <ProjectExecutionResults />
          </PrivateRoute>
        } />
        <Route path="/company/:companyId/project/:projectId/execution/:executionId/steps" element={
          <PrivateRoute>
            <ProjectExecutionSteps />
          </PrivateRoute>
        } />
        <Route path="/company/:companyId/project/:projectId/execution/:executionId" element={
          <PrivateRoute>
            <ReviewFilePage />
          </PrivateRoute>
        } />
        <Route path="/company/:companyId/project/:projectId/flow" element={
          <PrivateRoute>
            <FlowDesignerPage />
          </PrivateRoute>
        } />
        <Route path="/company/:companyId/project/:projectId" element={
          <PrivateRoute>
            <ProjectDetailPage />
          </PrivateRoute>
        } />
        <Route path="/company/:companyId" element={
          <PrivateRoute>
            <DashboardPage />
          </PrivateRoute>
        } />
        
        {/* Legacy routes for backward compatibility during transition */}
        <Route path="/project/:projectId/converter/:converterId/file/:fileId" element={
          <PrivateRoute>
            <ReviewFilePage />
          </PrivateRoute>
        } />
        <Route path="/project/:projectId/converter/:converterId" element={
          <PrivateRoute>
            <ConverterDetailPage />
          </PrivateRoute>
        } />
        <Route path="/project/:projectId/execution/:executionId" element={
          <PrivateRoute>
            <ReviewFilePage />
          </PrivateRoute>
        } />
        <Route path="/project/:projectId" element={
          <PrivateRoute>
            <ProjectDetailPage />
          </PrivateRoute>
        } />
        
        {/* Admin Company Selection - Main Dashboard */}
        <Route path="/" element={
          <PrivateRoute>
            <CompanySelectionPage />
          </PrivateRoute>
        } />
      </Routes>
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;
